/**
 * GridCrash — Anonymous Auth (Supabase)
 *
 * Gives every player a persistent anonymous identity so coins, unlocks,
 * and replays survive across sessions — with zero login UI.
 *
 * Rules:
 * - supabase-js is loaded lazily (dynamic import) so it never blocks
 *   first paint; the game is fully playable if it never loads.
 * - The session persists in localStorage via supabase-js defaults.
 *   Clearing site data permanently discards an anonymous identity.
 * - Everything here fails soft: offline, missing env vars, or a blocked
 *   request all resolve to null and the game carries on locally.
 *
 * The Netlify leaderboard proxy is unrelated to this module — public
 * leaderboard reads/writes stay on the server-side service key. This
 * client (anon key + RLS) is only for per-user data: profiles, replays.
 */

import type { SupabaseClient } from "@supabase/supabase-js";

let clientPromise: Promise<SupabaseClient | null> | null = null;

async function createLazyClient(): Promise<SupabaseClient | null> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  if (!url || !key) return null;

  try {
    const { createClient } = await import("@supabase/supabase-js");
    return createClient(url, key);
  } catch {
    return null;
  }
}

/** Lazy singleton Supabase client, or null when unconfigured/offline. */
export function getClient(): Promise<SupabaseClient | null> {
  clientPromise ??= createLazyClient();
  return clientPromise;
}

/**
 * Returns the current user id, signing in anonymously if needed.
 * Null means "no backend identity available" — callers must treat
 * that as a normal, non-error state.
 */
export async function ensureSession(): Promise<string | null> {
  const client = await getClient();
  if (!client) return null;

  try {
    const { data: existing } = await client.auth.getSession();
    if (existing.session) return existing.session.user.id;

    const { data, error } = await client.auth.signInAnonymously();
    if (error || !data.session) return null;
    return data.session.user.id;
  } catch {
    return null;
  }
}
